import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CHAPTERS } from "../lib/data";
import SEO from "../components/SEO";

/**
 * Welcome — clean artwork (no baked button). We add only:
 *  - A small green "Let's meet the herd" button on the dirt path
 *  - A transparent click target on the blank right-side board → opens Chapters
 *  - Barn doors that swing open on first load to reveal the pasture
 */
export default function Welcome() {
  const [open, setOpen] = useState(false);
  const [doorsOpen, setDoorsOpen] = useState(false);
  const [doorsGone, setDoorsGone] = useState(false);

  useEffect(() => {
    // Respect users who prefer reduced motion — skip the cinematic open.
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setDoorsOpen(true);
      setDoorsGone(true);
      return;
    }
    const t1 = setTimeout(() => setDoorsOpen(true), 3000);
    // After the animation finishes, drop the doors from the DOM
    const t2 = setTimeout(() => setDoorsGone(true), 5700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="relative w-screen min-h-screen flex items-center justify-center bg-[#2B1C12] overflow-hidden p-3 sm:p-5">
      <SEO
        title="The Salty Moo™ — Where Faith Finds Its Salt"
        description="A storybook pasture where real animals inspire stories of faith, family, laughter, and little everyday miracles. Step through the barn doors and meet the herd."
        path="/"
        image="https://thesaltymoo.com/welcome-scene.webp"
      />
      {/* Wrapper holds the artwork at its natural aspect ratio so nothing is cropped.
          Brown picture-frame border per user request (message 441). */}
      <div
        className="relative"
        style={{
          width: "min(100vw - 24px, 174vh)",
          aspectRatio: "1672 / 940",
          border: "10px solid #4B3A2F",
          borderRadius: "4px",
          boxShadow:
            "0 0 0 2px #2B1C12, 0 0 0 4px #6B4F3A, 0 30px 60px -20px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.06)",
          backgroundColor: "#4B3A2F",
        }}
      >
        <img
          src="/welcome-scene.webp"
          alt="Welcome to The Salty Moo"
          className="absolute inset-0 w-full h-full select-none"
          draggable="false"
        />

        {/* Official Circle SM Logo — mounted round plaque on the barn's
            upper front gable (occupies the space where the old separate
            cross + wooden 'SALTY MOO' sign used to sit). Preserves the
            uploaded artwork exactly; only a subtle drop-shadow is applied
            so it reads as physically attached to the barn wood. */}
        {/* Official Circle SM Logo — transparent black overlay on the barn's
            upper gable. Centered on the barn's true vertical centerline
            (roof peak straight down through doorway center ≈ x=75% of the
            welcome-scene artwork). Center-based positioning via
            translate(-50%,-50%) so `left`/`top` locate the logo's geometric
            center, not its top-left corner. */}
        <img
          data-testid="barn-gable-logo"
          src="/assets/sm-logo-white.webp"
          alt="The Salty Moo — Salt of the Herd"
          draggable="false"
          className="absolute select-none pointer-events-none"
          style={{
            left: "76.5%",
            top: "20.5%",
            width: "7.75%",
            aspectRatio: "1 / 1",
            objectFit: "contain",
            transform: "translate(-50%, -50%)",
          }}
        />

        {/* Left wooden sign — new brand line + salt block artwork.
            Content is centered as one vertical group inside the plaque. */}
        <div
          data-testid="left-sign-plaque"
          aria-label="Where Faith Finds Its Salt"
          className="absolute select-none pointer-events-none"
          style={{
            left: "8.85%",
            top: "52.1%",
            width: "14.6%",
            height: "27.5%",
          }}
        >
          {/* Inner wrapper: horizontally centered, visual center at 46% of
              plaque height so the group sits slightly above optical middle. */}
          <div
            className="flex flex-col items-center"
            style={{
              position: "absolute",
              left: "50%",
              top: "46%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              gap: "0.35vw",
            }}
          >
            <div
              className="text-center leading-[1.1]"
              style={{
                fontFamily: "'Bakerie', cursive",
                fontWeight: 900,
                color: "#000000",
                fontSize: "clamp(0.55rem, 1.55vw, 1.85rem)",
                letterSpacing: "0.02em",
                WebkitTextStroke: "0.5px #000000",
                whiteSpace: "nowrap",
              }}
            >
              <div>Where Faith</div>
              <div>Finds Its Salt</div>
            </div>
            <img
              src="/assets/salt-block-shaker.webp"
              alt=""
              aria-hidden="true"
              draggable="false"
              className="block max-w-full"
              style={{
                width: "82%",
                height: "auto",
                transform: "none",
                filter: "drop-shadow(0 2px 3px rgba(60,35,15,0.28))",
              }}
            />
          </div>
        </div>

        {/* His Story, Our Yes — wooden sign on the right fence post */}
        <Link
          to="/his-story"
          data-testid="begin-journey-btn"
          aria-label="His Story, Our Yes"
          className="absolute group flex items-center justify-center rope-sway-trigger"
          style={{ left: "81%", top: "56%", width: "13%", height: "22%" }}
        >
          <div className="flex flex-col items-center justify-center transition-transform duration-700 group-hover:-translate-y-[2px] rope-sway-target">
            <div
              className="text-center leading-[1.1]"
              style={{
                fontFamily: "'Bakerie', cursive",
                fontWeight: 900,
                color: "#000000",
                fontSize: "clamp(0.6rem, 1.7vw, 2.05rem)",
                letterSpacing: "0.02em",
                WebkitTextStroke: "0.5px #000000",
              }}
            >
              <div style={{ whiteSpace: "nowrap" }}>His Story,</div>
              <div style={{ whiteSpace: "nowrap" }}>Our Yes</div>
            </div>
          </div>
        </Link>
      </div>

      {/* Barn doors swing-open overlay — first-load only */}
      {!doorsGone && (
        <div
          aria-hidden
          className="fixed inset-0 z-[70] pointer-events-none"
          style={{ perspective: "1800px" }}
          data-testid="barn-doors-overlay"
        >
          {/* Full-viewport dark backdrop — the "outside of the barn" so nothing
              of the pasture is visible while the doors are closed. Fades out
              as the doors swing open. */}
          <div
            aria-hidden
            data-testid="barn-doors-backdrop"
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 80% at center, #3a251a 0%, #2b1c12 60%, #1a1008 100%)",
              opacity: doorsOpen ? 0 : 1,
              transition: "opacity 1400ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
          {/* Slow cinematic zoom-out during the 3s closed state — curtain-rising feel */}
          <div
            className="absolute inset-0 barn-zoom"
            data-testid="barn-doors-zoom"
          >
          {/* Vignette — soft radial fade to dark on the outer edges,
              draws the eye toward the SM logo in the center.
              Fades out as the doors open. */}
          <div
            aria-hidden
            data-testid="barn-vignette"
            className="absolute inset-0"
            style={{
              /* Softer vignette + lifted bottom so the pasture (fence + bluebonnets)
                 in the welcome-scene artwork can breathe on either side of the doors */
              background:
                "radial-gradient(ellipse 55% 90% at center 30%, rgba(43,28,18,0) 0%, rgba(43,28,18,0.35) 55%, rgba(43,28,18,0.72) 100%)",
              opacity: doorsOpen ? 0 : 1,
              transition: "opacity 1400ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
          <img
            src="/assets/barn-door-left.webp"
            alt=""
            className="absolute top-0 select-none"
            draggable="false"
            style={{
              /* Anchored to the center seam so both doors always meet in the middle
                 regardless of viewport size. Capped width leaves symmetric pasture
                 peek on the outer sides. */
              right: "50%",
              width: "min(40vw, 50vh)",
              height: "100%",
              objectFit: "contain",
              objectPosition: "right center",
              transformOrigin: "left center",
              transform: doorsOpen
                ? "rotateY(-118deg)"
                : "rotateY(0deg)",
              transition:
                "transform 2400ms cubic-bezier(0.55, 0.05, 0.25, 1)",
              boxShadow: doorsOpen
                ? "12px 0 30px rgba(0,0,0,0.5)"
                : "0 0 0 rgba(0,0,0,0)",
              backfaceVisibility: "hidden",
              willChange: "transform",
            }}
          />
          <img
            src="/assets/barn-door-right.webp"
            alt=""
            className="absolute top-0 select-none"
            draggable="false"
            style={{
              /* Mirror of left door — hugs the center seam from the right */
              left: "50%",
              width: "min(40vw, 50vh)",
              height: "100%",
              objectFit: "contain",
              objectPosition: "left center",
              transformOrigin: "right center",
              transform: doorsOpen
                ? "rotateY(118deg)"
                : "rotateY(0deg)",
              transition:
                "transform 2400ms cubic-bezier(0.55, 0.05, 0.25, 1)",
              boxShadow: doorsOpen
                ? "-12px 0 30px rgba(0,0,0,0.5)"
                : "0 0 0 rgba(0,0,0,0)",
              backfaceVisibility: "hidden",
              willChange: "transform",
            }}
          />
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-[#2C2724]/55 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={() => setOpen(false)}
          data-testid="chapters-overlay"
        >
          <div
            className="parchment-deep page-shadow max-w-lg w-full rounded-md border border-[#A87149] p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-script text-3xl text-[#4B3A2F] text-center">
              Choose a chapter
            </p>
            <div className="gold-rule my-5" />
            <ul className="space-y-1">
              {CHAPTERS.map((c) => (
                <li key={c.path}>
                  <Link
                    to={c.path}
                    onClick={() => setOpen(false)}
                    className="flex items-baseline gap-4 px-3 py-3 border-b border-[#E7DFD5]/70 text-[#2C2724] hover:text-[#4B3A2F] transition-colors"
                  >
                    <span className="font-display italic text-[#D62839] w-8">
                      {c.short}
                    </span>
                    <span className="font-display text-2xl">{c.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-center text-sm text-[#6D5A50] italic">
              Where Faith Finds Its Salt.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
